type RgbColor = { r: number; g: number; b: number };

export type HealthCheckSeverityLabel = 'Critical' | 'High' | 'Medium' | 'Low';

function interpolateColor(left: RgbColor, right: RgbColor, factor: number): RgbColor {
  const t = Math.max(0, Math.min(1, factor));

  return {
    r: Math.round(left.r + (right.r - left.r) * t),
    g: Math.round(left.g + (right.g - left.g) * t),
    b: Math.round(left.b + (right.b - left.b) * t),
  };
}

function toRgbStyle(color: RgbColor): string {
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

function resolveBadgeColors(scorePercentage: number): { backgroundColor: string; color: string } {
  const backgroundRed = { r: 255, g: 241, b: 242 };
  const backgroundOrange = { r: 255, g: 247, b: 237 };
  const backgroundGreen = { r: 236, g: 253, b: 245 };

  const textRed = { r: 190, g: 24, b: 93 };
  const textOrange = { r: 194, g: 65, b: 12 };
  const textGreen = { r: 4, g: 120, b: 87 };

  const normalized = Math.max(0, Math.min(100, scorePercentage));

  if (normalized <= 50) {
    return {
      backgroundColor: toRgbStyle(backgroundRed),
      color: toRgbStyle(textRed),
    };
  }

  if (normalized <= 75) {
    const factor = (normalized - 50) / (75 - 50);
    return {
      backgroundColor: toRgbStyle(interpolateColor(backgroundRed, backgroundOrange, factor)),
      color: toRgbStyle(interpolateColor(textRed, textOrange, factor)),
    };
  }

  if (normalized <= 90) {
    const factor = (normalized - 75) / (100 - 75);
    return {
      backgroundColor: toRgbStyle(interpolateColor(backgroundOrange, backgroundGreen, factor)),
      color: toRgbStyle(interpolateColor(textOrange, textGreen, factor)),
    };
  }

  return {
    backgroundColor: toRgbStyle(backgroundGreen),
    color: toRgbStyle(textGreen),
  };
}

function resolveSeverityLabel(scorePercentage: number): HealthCheckSeverityLabel {
  const normalized = Math.max(0, Math.min(100, scorePercentage));

  if (normalized <= 50) {
    return 'Critical';
  }

  if (normalized <= 75) {
    return 'High';
  }

  if (normalized <= 90) {
    return 'Medium';
  }

  return 'Low';
}

export function resolveHealthCheckScorePresentation(scorePercentage: number): {
  label: HealthCheckSeverityLabel;
  style: { backgroundColor: string; color: string };
} {
  return {
    label: resolveSeverityLabel(scorePercentage),
    style: resolveBadgeColors(scorePercentage),
  };
}
